import { Helmet } from 'react-helmet-async';
import { Box, Button, Card, CardContent, Grid, Stack, Typography, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { PATH_DASHBOARD } from '../../routes/paths';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { getFirmwareDetail, deleteFirmware, triggerOTA } from '../../api';
import { useSnackbar } from 'src/components/snackbar';
import dayjs from 'dayjs';
import { fData } from '../../utils/formatNumber';
import Iconify from '../../components/iconify';
import ConfirmDialog from 'src/components/confirm-dialog';

export default function OTAManageDetail() {
  const [firmware, setFirmware] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [openTriggerDialog, setOpenTriggerDialog] = useState(false);
  const [triggerData, setTriggerData] = useState({ device_id: '' });
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const firmwareId = searchParams.get('id');

  const handleGetFirmwareDetail = React.useCallback(async () => {
    try {
      setLoading(true);
      const res: any = await getFirmwareDetail(firmwareId!);
      if (res.code === 200) {
        setFirmware(res.data);
      } else {
        enqueueSnackbar(res.message || '获取固件详情失败！', { variant: 'error' });
      }
    } catch (error) {
      console.error('Get firmware detail error:', error);
      enqueueSnackbar('获取固件详情失败！', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [firmwareId, enqueueSnackbar]);

  useEffect(() => {
    if (firmwareId) {
      handleGetFirmwareDetail();
    }
  }, [firmwareId, handleGetFirmwareDetail]);

  const handleTriggerOTA = async () => {
    // 验证输入框是否有值
    if (!triggerData.device_id || triggerData.device_id.trim() === '') {
      enqueueSnackbar('请输入设备序列号！', { variant: 'error' });
      return;
    }

    try {
      const data: any = {
        sn: triggerData.device_id.trim()
      };

      const res: any = await triggerOTA(firmwareId!, data);
      if (res.code === 200 || res.code === 202) {
        enqueueSnackbar('OTA升级已触发！');
        setOpenTriggerDialog(false);
        setTriggerData({ device_id: '' });
      } else {
        enqueueSnackbar(res.message || '触发失败！', { variant: 'error' });
      }
    } catch (error) {
      console.error('Trigger OTA error:', error);
      enqueueSnackbar('触发失败！', { variant: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      const res: any = await deleteFirmware(firmwareId!);
      if (res.code === 200) {
        enqueueSnackbar('固件删除成功！');
        setOpenDeleteConfirm(false);
        // 跳转回列表页
        window.location.href = PATH_DASHBOARD.otaManage.list;
      } else {
        enqueueSnackbar(res.message || '删除失败！', { variant: 'error' });
      }
    } catch (error) {
      console.error('Delete firmware error:', error);
      enqueueSnackbar('删除失败！', { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>加载中...</Typography>
      </Box>
    );
  }

  if (!firmware) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          固件不存在或已被删除
        </Typography>
        <Button component={RouterLink} to={PATH_DASHBOARD.otaManage.list} sx={{ mt: 2 }}>
          返回列表
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>固件详情 - {firmware.version}</title>
      </Helmet>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h2">
          固件详情
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            component={RouterLink}
            to={PATH_DASHBOARD.otaManage.list}
            startIcon={<Iconify icon="eva:arrow-back-fill" />}
          >
            返回列表
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenTriggerDialog(true)}
            startIcon={<Iconify icon="eva:upload-fill" />}
          >
            触发OTA升级
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setOpenDeleteConfirm(true)}
            startIcon={<Iconify icon="eva:trash-2-fill" />}
          >
            删除固件
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                基本信息
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    版本号
                  </Typography>
                  <Typography variant="h6">{firmware.version}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    硬件版本
                  </Typography>
                  <Typography variant="h6">{firmware.hardware_version}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    文件名
                  </Typography>
                  <Typography variant="body1">{firmware.file_title}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    文件大小
                  </Typography>
                  <Typography variant="body1">{fData(firmware.file_size)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    创建时间
                  </Typography>
                  <Typography variant="body1">
                    {dayjs(firmware.created_at).format('YYYY-MM-DD HH:mm:ss')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    发布说明
                  </Typography>
                  <Typography variant="body1">
                    {firmware.release_notes || '无'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                文件信息
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    SHA256校验和
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      wordBreak: 'break-all',
                    }}
                  >
                    {firmware.checksum}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    固件ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {firmware.id}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                操作说明
              </Typography>
              <Stack spacing={2}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>触发OTA升级：</strong>
                  </Typography>
                  <Typography variant="body2" component="div">
                    • 可以指定单个或多个设备升级
                  </Typography>
                  <Typography variant="body2" component="div">
                    • 每行输入一个设备序列号
                  </Typography>
                </Alert>

                <Alert severity="warning">
                  <Typography variant="body2">
                    <strong>删除固件：</strong>
                  </Typography>
                  <Typography variant="body2" component="div">
                    • 此操作不可恢复
                  </Typography>
                  <Typography variant="body2" component="div">
                    • 会同时删除文件存储
                  </Typography>
                  <Typography variant="body2" component="div">
                    • 请谨慎操作
                  </Typography>
                </Alert>

                <Alert severity="success">
                  <Typography variant="body2">
                    <strong>OTA传输：</strong>
                  </Typography>
                  <Typography variant="body2" component="div">
                    • 通过PropertySetWorkflow推送
                  </Typography>
                  <Typography variant="body2" component="div">
                    • 属性路径：core.ota
                  </Typography>
                  <Typography variant="body2" component="div">
                    • 工作流超时：40秒
                  </Typography>
                </Alert>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 触发OTA升级对话框 */}
      <Dialog open={openTriggerDialog} onClose={() => setOpenTriggerDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>触发OTA升级</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            请输入要升级的设备序列号
          </Typography>
          <Stack spacing={3}>
            <TextField
              label="设备序列号"
              value={triggerData.device_id}
              onChange={(e) => setTriggerData({ device_id: e.target.value })}
              placeholder="例如: YZNVT01252010001"
              helperText="请输入单个设备序列号"
            />
            <Alert severity="warning">
              <Typography variant="body2">
                请至少输入一个设备序列号
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTriggerDialog(false)}>取消</Button>
          <Button
            variant="contained"
            onClick={handleTriggerOTA}
          >
            触发升级
          </Button>
        </DialogActions>
      </Dialog>

      {/* 删除确认对话框 */}
      <ConfirmDialog
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        title="删除固件"
        content="确定删除此固件吗？此操作不可恢复，将同时删除文件存储！"
        action={
          <Button variant="contained" color="error" onClick={handleDelete}>
            确定删除
          </Button>
        }
      />
    </>
  );
}
